import { partyBreakdownFor } from './similarity'
import { partyRecordOf, partyRoleFor } from './partyLabel'

/**
 * Adapter: edit-wizard state → the props CM's HourlyInvoice components take.
 *
 * WHY REUSE THOSE COMPONENTS RATHER THAN DRAW OUR OWN
 * `HourlyInvoice/InvoicePreview` and `HourlyInvoice/InvoiceSummary` are the same
 * pair the hourly CREATE flow renders, so the document a case manager approves
 * here is what the client receives. A hand-drawn approximation would drift the
 * first time the invoice layout changes.
 *
 * WHY THIS IS A REWRITE OF SCALE'S ADAPTER RATHER THAN A PORT
 * Scale's components read HourlyInvoiceContext and write derived totals back
 * through Formik's `setFieldValue`; CM's take plain props and are pure. So this
 * file returns (a) a `values` object for the small parts both components read
 * off `values`, and (b) an explicit prop bag for everything CM passes in.
 * Nothing here is submitted — the payload is built from the wizard's own state.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100 + Number.EPSILON) / 100

/**
 * Build the preview payload for ONE invoice row.
 *
 * `selectedOption: 'one'` on purpose even when the wizard is editing both sides:
 * each invoice is a separate document with its own recipient and its own total,
 * so the preview is rendered once per row rather than as a combined statement.
 * Step 3 switches between them.
 *
 * `shareOverride` is how PARTY SCOPE is expressed, and it is passed ONLY when
 * editScope === 'party'. In session scope the stored share must be resolved
 * from the row — forcing share 1 there would preview a 3-party $900 session as
 * $900 on every tab while Stripe bills $300 each.
 */
export const buildPreviewValues = ({
    row,
    items,
    dueDate,
    notes,
    caseInfo,
    clientInfo,
    shareOverride,
}) => {
    /* `items` is the case-wide mediation LEDGER, not this party's bill — each
     * party is charged a share of it. */
    const ledger = round2(
        (items || []).reduce((s, it) => s + (Number(it?.amount) || 0), 0)
    )

    const breakdown = partyBreakdownFor(row, caseInfo, ledger, shareOverride)

    /* LINES ARE NOT RESCALED, AND THE CREDIT LINE IS NOT OPTIONAL.
     *
     * The invoice Stripe actually issues prints the FULL ledger lines and then a
     * negative "TOTAL DUE BY EACH PARTY (~pct%)" credit that nets down to this
     * party's base. Showing the real lines is therefore both what the case
     * manager typed AND what the client receives.
     *
     * CM's InvoicePreview draws its own built-in credit row, but only when
     * `values.selectedOption !== 'one'` — and every document here IS 'one'. So
     * the row has to be appended to the line items instead, or the lines never
     * reconcile to the total.
     *
     * `_isCredit` MARKS IT AS NOT AN HOURLY LINE. Both shared panes format every
     * entry in `values.items` as hours × rate — InvoicePreview appends
     * "(h:mm hours)" to the description and InvoiceSummary prints
     * "h:mm hrs × $rate/hr". Rendered that way a credit reads as
     * "TOTAL DUE BY EACH PARTY (~33%) (1:00 hours) … $-600.00/hr": one hour of
     * work at minus six hundred dollars an hour. The flag lets those panes draw
     * it the way InvoicePreview already draws its own built-in credit row — no
     * hours, no unit price, amount as "- $600.00".
     *
     * `quantity: null` because there is no quantity, rather than a 1 that only
     * looks right. The panes gate on the FLAG, never on `amount < 0`, so the
     * hourly CREATE flow that shares these components is untouched. */
    const creditAmount = round2(breakdown.base - ledger)
    const linesWithCredit =
        breakdown.share === 1 || creditAmount === 0
            ? items || []
            : [
                  ...(items || []),
                  {
                      id: '__party_share_credit__',
                      description: `TOTAL DUE BY EACH PARTY (~${breakdown.pct}%)`,
                      quantity: null,
                      rate: creditAmount,
                      amount: creditAmount,
                      _isCredit: true,
                  },
              ]

    return {
        // Read by InvoicePreview.
        invoice_number: row?.invoice_number || '',
        date: new Date().toISOString().slice(0, 10),
        due_date: dueDate || '',
        selectedOption: 'one',
        selectedParty: row?.client_id ?? null,
        items: linesWithCredit,

        /* Read by InvoiceSummary. `subtotal` feeds its TOTAL AMOUNT card, which
         * is what THIS PARTY pays — not the session ledger. InvoicePreview does
         * not read `subtotal` at all; its totals come from the props below. */
        subtotal: breakdown.total,
        notes: notes || '',
        additional_notes: notes || '',

        // Informational; kept so the two panes cannot disagree about the money.
        grand_total: breakdown.total,
        admin_fee: breakdown.fee,
        is_admin_fee_added_in_line_item: breakdown.adminInAmount && breakdown.fee > 0,
        includeProcessingFee: false,
    }
}

/**
 * The bill-to record, FLAT, because CM's ClientInformation / InvoiceSummary /
 * InvoicePreview all consume a flat client (name / email / phone / address)
 * even though ClientInformation EMITS the nested `{ client_information: {…} }`
 * shape the update endpoint wants.
 *
 * Whatever the case manager typed on step 1 is overlaid here. Building this
 * from `caseInfo` alone is why the document used to keep showing the ORIGINAL
 * name and email after an edit — the Bill To block silently disagreeing with
 * the form that had just been filled in.
 */
export const buildRecipient = ({ row, caseInfo, clientInfo, mediatorRecord }) => {
    const party = partyRecordOf(row, caseInfo)
    const ci = clientInfo?.client_information || {}
    const fallback = row?.clients || {}
    const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '') ?? ''

    return {
        client_id: row?.client_id ?? party?.client_id ?? null,
        name: pick(ci.name, party?.name, fallback.name),
        email: pick(ci.email, party?.email, fallback.email),
        phone: pick(
            ci.phone_number,
            party?.phone,
            party?.phone_number,
            fallback.phone_number,
            fallback.phone
        ),
        address: pick(ci.address, party?.address, fallback.address),
        // "Plaintiff 2" — InvoiceSummary prints this as the recipient's label.
        role: partyRoleFor(row, caseInfo, mediatorRecord),
        /* InvoiceSummary renders a CC block when this holds more than one
         * address; it accepts a comma string or an array. */
        invoice_emails: clientInfo?.invoice_emails ?? '',
    }
}

/**
 * The `mediator` prop CM's InvoicePreview expects, assembled from the
 * getAccountData-shaped record the edit-context endpoint returns.
 *
 * CM's preview reads `business_name`, `first_name`/`last_name`, `stripeLogo`,
 * `stripeAddress` and `stripe_session.business_address` off the TOP LEVEL. The
 * endpoint nests the Stripe fields under `stripe_session` and the name under
 * `profile`, so without this mapping the From block renders empty and no logo
 * appears.
 *
 * `stripe_session` can legitimately be null (UAT case 1411) — Scale then
 * refuses the edit with "This mediator is not connected to Stripe", so this
 * only has to degrade, not guess.
 */
export const buildPreviewMediator = (mediatorRecord, caseInfo) => {
    const raw = mediatorRecord?.stripe_session || null
    const fromCase = caseInfo?.mediator_id || null
    const profile = mediatorRecord?.profile || null

    /* `business_address` is a Stripe address OBJECT on most rows but a plain
     * string on some. The preview walks `.line1` / `.city` and, because a
     * non-empty string is still truthy, a string address renders as NOTHING
     * while also suppressing the `stripeAddress` fallback beneath it. Splitting
     * the two shapes here keeps that fix out of the shared component. */
    const addressObject =
        raw?.business_address && typeof raw.business_address === 'object'
            ? raw.business_address
            : null
    const addressString =
        typeof raw?.business_address === 'string' ? raw.business_address : null
    const s = raw ? { ...raw, business_address: addressObject } : null

    return {
        ...(mediatorRecord || {}),
        business_name:
            s?.business_name ||
            profile?.name ||
            [fromCase?.first_name, fromCase?.last_name].filter(Boolean).join(' '),
        first_name: profile?.first_name || fromCase?.first_name || '',
        last_name: profile?.last_name || fromCase?.last_name || '',
        email: s?.business_email || mediatorRecord?.email || fromCase?.email || '',
        phone: s?.business_phone_no || mediatorRecord?.phone || fromCase?.phone || '',
        // Object form ({ line1, city, … }) — the preview walks its parts.
        stripe_session: s,
        stripeLogo: raw?.stripe_logo || mediatorRecord?.photo_url || null,
        // String form — the preview's fallback when there is no address object.
        stripeAddress: raw?.stripe_address || addressString || null,
        invoice_footer: mediatorRecord?.invoice_footer ?? fromCase?.invoice_footer ?? null,
        cancellation_policy:
            mediatorRecord?.cancellation_policy ?? fromCase?.cancellation_policy ?? null,
        additional_information:
            mediatorRecord?.additional_information ??
            fromCase?.additional_information ??
            null,
        mediatorSettings:
            mediatorRecord?.mediatorSettings ?? fromCase?.mediator_settings ?? null,
    }
}

/**
 * Every remaining prop the two panes take, derived from ONE breakdown so the
 * summary and the document cannot disagree.
 *
 * `perPartyPercentage` comes from the computed breakdown, never a hardcoded
 * 100 — it is what the "~N%" credit line is labelled with.
 *
 * `splitFactor` is 1 and `processingFeeToClient` is false ON PURPOSE:
 *  - the per-party split is already expressed by the credit line above, so
 *    dividing again would halve an already-halved figure;
 *  - the reissue defers the card fee to the payment gateway, so the revised
 *    invoice does not carry that line. Printing it would show the client a
 *    charge that is not on their bill.
 */
export const buildPreviewProps = ({ row, caseInfo, clientInfo, mediatorRecord, breakdown }) => {
    const recipient = buildRecipient({ row, caseInfo, clientInfo, mediatorRecord })
    return {
        recipient,
        mediator: buildPreviewMediator(mediatorRecord, caseInfo),
        caseData: caseInfo,
        splitFactor: 1,
        perPartyShare: breakdown.base,
        perPartyPercentage: breakdown.pct,
        processingFeeToClient: false,
        adminFee: breakdown.fee,
        isAdminFeeAddedInLineItem: breakdown.adminInAmount && breakdown.fee > 0,
    }
}
